// ============================================================
// VCS.Blur — Hintergrund-Weichzeichner (MediaPipe Vision)
// ============================================================
window.VCS = window.VCS || {};

VCS.Blur = class {

    // ── Konstanten ───────────────────────────────────────────

    static VISION_BASE = '/vendor/mediapipe-vision';
    static MODEL_URL   = '/vendor/mediapipe-vision/selfie_segmenter.tflite';
    static BLUR_PX    = parseInt(localStorage.getItem('blur-px'), 10) || 20;
    static CANVAS_FPS = 30;

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._segmenter   = null;
        this._ready       = false;
        this._loading     = false;
        this._tempCanvas  = null;
        this._tempCtx     = null;

        // View-Cache
        this._view = {
            previewVideo: $('#previewVideo'),
            localVideo:   $('#localVideo'),
        };
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    // Video-Pipeline neu aufbauen (mit oder ohne Blur)
    async rebuild() {
        if (!VCS.state.baseStream) {
            return;
        }

        let trueBlurActive = false;
        if (VCS.state.blurEnabled) {
            trueBlurActive = await this._activateTrueBlur();
            if (!trueBlurActive) {
                VCS.state.blurFallbackActive = true;
                VCS.ui?.showToast?.(t('blurFallback'), 'warning', 2200);
                this.stopPipeline();
                VCS.state.localStream = VCS.state.baseStream;
            }
        } else {
            this.stopPipeline();
            VCS.state.localStream = VCS.state.baseStream;
        }

        if (trueBlurActive) {
            VCS.state.blurFallbackActive = false;
        }

        const baseVideo = VCS.state.baseStream.getVideoTracks()[0];
        if (baseVideo) {
            baseVideo.enabled = !VCS.state.videoOff;
        }

        const activeTrack = VCS.state.localStream?.getVideoTracks()[0] || null;
        if (activeTrack) {
            activeTrack.enabled = !VCS.state.videoOff;
            this.replacePeerVideoTrack(activeTrack);
        }

        this._view.previewVideo.prop('srcObject', VCS.state.localStream);
        this._view.localVideo.prop('srcObject',   VCS.state.localStream);
        this._applyBlurMode();
    }

    // Pipeline stoppen und alle Blur-Ressourcen freigeben
    stopPipeline() {
        if (VCS.state.blurSendTimer) {
            cancelAnimationFrame(VCS.state.blurSendTimer);
            VCS.state.blurSendTimer = null;
        }

        if (VCS.state.processedVideoTrack) {
            VCS.state.processedVideoTrack.stop();
            VCS.state.processedVideoTrack = null;
        }
        if (VCS.state.blurSourceVideo) {
            VCS.state.blurSourceVideo.pause();
            VCS.state.blurSourceVideo.srcObject = null;
            VCS.state.blurSourceVideo.remove();
            VCS.state.blurSourceVideo = null;
        }
        VCS.state.blurCanvas = null;
        VCS.state.blurCtx    = null;
        this._tempCanvas     = null;
        this._tempCtx        = null;
    }

    // Video-Track im laufenden PeerConnection ersetzen
    replacePeerVideoTrack(nextTrack) {
        VCS.webrtc?.replacePeerVideoTrack(nextTrack);
        if (VCS.state.groupCallActive) {
            VCS.webrtc?.replaceGroupVideoTrack(nextTrack);
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Kein eigenständiger Setup — wird von anderen Modulen aufgerufen
    }

    // ── Private Methoden ─────────────────────────────────────

    async _activateTrueBlur() {
        if (!VCS.state.baseStream) {
            return false;
        }
        const ok = await this._ensureSegmenter();
        if (!ok) {
            return false;
        }

        this.stopPipeline();

        const baseTrack = VCS.state.baseStream.getVideoTracks()[0];
        if (!baseTrack) {
            return false;
        }

        const { width = 640, height = 360 } = baseTrack.getSettings();

        // Quell-Video
        VCS.state.blurSourceVideo = document.createElement('video');
        Object.assign(VCS.state.blurSourceVideo, {
            autoplay: true, muted: true, playsInline: true,
        });
        VCS.state.blurSourceVideo.srcObject = new MediaStream([baseTrack]);
        await VCS.state.blurSourceVideo.play().catch(() => {});

        // Ausgabe-Canvas
        VCS.state.blurCanvas       = document.createElement('canvas');
        VCS.state.blurCanvas.width = width;
        VCS.state.blurCanvas.height = height;
        VCS.state.blurCtx          = VCS.state.blurCanvas.getContext('2d');

        // Hilfs-Canvas für Person-Maske
        this._tempCanvas       = document.createElement('canvas');
        this._tempCanvas.width = width;
        this._tempCanvas.height = height;
        this._tempCtx          = this._tempCanvas.getContext('2d');

        // Render-Loop mit requestAnimationFrame
        const renderFrame = () => {
            if (!VCS.state.blurCanvas || !this._ready) {
                return;
            }
            const video = VCS.state.blurSourceVideo;
            if (!video || video.paused || video.ended || video.readyState < 2) {
                VCS.state.blurSendTimer = requestAnimationFrame(renderFrame);
                return;
            }
            this._segmenter.segmentForVideo(video, performance.now(), (result) => {
                if (result.categoryMask) {
                    this._composite(video, result.categoryMask.getAsFloat32Array(), width, height);
                }
            });
            VCS.state.blurSendTimer = requestAnimationFrame(renderFrame);
        };
        VCS.state.blurSendTimer = requestAnimationFrame(renderFrame);

        // Output-Stream aus Canvas
        const outStream = VCS.state.blurCanvas.captureStream(VCS.Blur.CANVAS_FPS);
        VCS.state.processedVideoTrack = outStream.getVideoTracks()[0] || null;
        if (!VCS.state.processedVideoTrack) {
            return false;
        }
        VCS.state.processedVideoTrack.enabled = !VCS.state.videoOff;

        VCS.state.localStream = new MediaStream([
            VCS.state.processedVideoTrack,
            ...VCS.state.baseStream.getAudioTracks(),
        ]);
        this.replacePeerVideoTrack(VCS.state.processedVideoTrack);
        return true;
    }

    // Segmenter laden (GPU → CPU Fallback)
    async _ensureSegmenter() {
        if (this._ready) {
            return true;
        }
        if (this._loading) {
            return false;
        }
        this._loading = true;

        try {
            const vision = await import(VCS.Blur.VISION_BASE + '/vision_bundle.mjs');
            const { FilesetResolver, ImageSegmenter } = vision;

            const wasmFileset = await FilesetResolver.forVisionTasks(
                VCS.Blur.VISION_BASE + '/wasm'
            );

            // GPU-Delegate versuchen, bei Fehler CPU-Fallback
            for (const delegate of ['GPU', 'CPU']) {
                try {
                    this._segmenter = await ImageSegmenter.createFromOptions(wasmFileset, {
                        baseOptions: {
                            modelAssetPath: VCS.Blur.MODEL_URL,
                            delegate,
                        },
                        runningMode:        'VIDEO',
                        outputCategoryMask:  true,
                        outputConfidenceMasks: false,
                    });
                    this._ready   = true;
                    this._loading = false;
                    return true;
                } catch (e) {
                    console.warn('ImageSegmenter (' + delegate + ') fehlgeschlagen:', e);
                }
            }

            this._loading = false;
            return false;
        } catch (err) {
            console.error('MediaPipe Vision konnte nicht geladen werden:', err);
            this._loading = false;
            return false;
        }
    }

    // Compositing: Hintergrund weichzeichnen, Person scharf darüber
    _composite(video, mask, w, h) {
        const ctx     = VCS.state.blurCtx;
        const tempCtx = this._tempCtx;
        if (!ctx || !tempCtx) {
            return;
        }

        // 1. Person scharf auf Hilfs-Canvas zeichnen
        tempCtx.drawImage(video, 0, 0, w, h);

        // 2. Maske als Alpha anwenden (1.0 = Person, 0.0 = Hintergrund)
        const imageData = tempCtx.getImageData(0, 0, w, h);
        const data = imageData.data;
        for (let i = 0; i < mask.length; i++) {
            data[i * 4 + 3] = (1 - mask[i]) * 255;
        }
        tempCtx.putImageData(imageData, 0, 0);

        // 3. Hintergrund weichgezeichnet auf Ausgabe-Canvas
        ctx.filter = 'blur(' + VCS.Blur.BLUR_PX + 'px)';
        ctx.drawImage(video, 0, 0, w, h);
        ctx.filter = 'none';

        // 4. Scharfe Person darüber compositen
        ctx.drawImage(this._tempCanvas, 0, 0);
    }

    _applyBlurMode() {
        const softBlur = VCS.state.blurEnabled && VCS.state.blurFallbackActive;
        this._view.previewVideo.toggleClass('video-soft-blur', softBlur);
        this._view.localVideo.toggleClass('video-soft-blur', softBlur);
    }
};
